import React, { useCallback, useEffect, useMemo, useState, useRef, Suspense, lazy } from "react";
import { debounce } from "lodash";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Button, { ButtonVariant } from "../../atoms/Button";
import Icon from "../../atoms/Icon";
import Text from "../../atoms/Text";
import InfoIconButton from "../../atoms/InfoIconButton";
import {
  AIReview,
  AIReviewStatus,
  DrugSearchResult,
  getPromptTypeKoreanName,
  Prescription,
} from "../../../common/models/types";
import { getPillsByItemCodes } from "../../../api/drugService";
import { calculateAge } from "../../../utils/TimeUtil";
import { collectSnapDrugs, pillsNameFallback } from "../../../utils/drugImageUtils";
import ReviewSection from "./ReviewSection";
import { useReviewToc } from "./ReviewSection/useReviewToc";
import TocDropdown from "./ReviewSection/components/TocDropdown";
import parse from "html-react-parser";
import { log, LogLevel } from "utils/LogUtil";
import EvidenceSection from "./EvidenceSection";
import { PanelGroup, Panel, PanelResizeHandle, ImperativePanelHandle } from "react-resizable-panels";
import { Flex, Spinner, ToggleButton } from "components/atoms";
import { eventLog } from "utils/AmplitudeUtil";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import MemoEditForm from "components/molecules/MemoEditForm";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { reviewContentTransform } from "../../../utils/reviewContentTransform";
import { updateAIReviewEdited } from "../../../api/aiReviewService";
import { processBase64Images } from "../../../api/uploadService";
import { buildHospitalAliasMapFromGroups } from "../../../utils/extractDepartment";
import { convertHospitalButtonsToSpans, unwrapPrescriptionButtons } from "../../../utils/hospitalDomUtils";
// ConfirmManager import 제거 - 더 이상 사용하지 않음
import { ToastManager } from "../../organisms/CommonUI";
import PatientInfoDisplay from "../../molecules/PatientInfoDisplay";
import PrescriptionTableDisplay from "../../molecules/PrescriptionTableDisplay";
import { useNavigationBlocker } from "../../../hooks/useNavigationBlocker";
import { StreamDisconnection } from "../../../hooks/useAIReviewStream";
import ReferenceInfoModal from "../../molecules/ReferenceInfoModal";

// 처방이력 뷰어는 ATC 분류표(~59KB) 등 무거운 의존성을 품어, 버튼으로 열 때만 로드(lazy)한다.
const PrescriptionHistoryViewerModal = lazy(
  () => import("../../organisms/PrescriptionHistoryViewer/PrescriptionHistoryViewerModal")
);

// 병원·처방전 버튼 클릭 시 좌측 패널이 대상 그룹으로 스크롤될 때 컨테이너 맨 위에 남길 상단 여유(px).
// 컨테이너 자체 padding이 16px이라 그보다 커야 여백으로 인지되고, 바로 위 그룹과의 경계가 보인다.
const SCROLL_TOP_GAP = 24;

const OuterContainer = styled.div<{ $isFullscreen?: boolean }>`
  padding: 16px;
  height: ${({ $isFullscreen }) =>
    $isFullscreen ? '100vh' : 'calc(100vh - 103px)'
  };
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: ${({ $isFullscreen }) => ($isFullscreen ? 'fixed' : 'relative')};
  top: ${({ $isFullscreen }) => ($isFullscreen ? '0' : 'auto')};
  left: ${({ $isFullscreen }) => ($isFullscreen ? '0' : 'auto')};
  right: ${({ $isFullscreen }) => ($isFullscreen ? '0' : 'auto')};
  bottom: ${({ $isFullscreen }) => ($isFullscreen ? '0' : 'auto')};
  z-index: ${({ $isFullscreen }) => ($isFullscreen ? '9999' : 'auto')};
  background: ${({ $isFullscreen }) => ($isFullscreen ? '#fff' : 'transparent')};
  transition: all 0.3s ease-in-out;
`;

const TopHeader = styled.div`
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 6px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
`;

const ReportTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  margin: 0;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    border-color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }

  &.favorite {
    background: ${COLOR_SYSTEM.get("Yellow")?.[50]};
    border-color: ${COLOR_SYSTEM.get("Yellow")?.[300]};
  }

  &.delete:hover {
    background: ${COLOR_SYSTEM.get("Red")?.[50]};
    border-color: ${COLOR_SYSTEM.get("Red")?.[300]};
  }
`;

const MemoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 6px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  max-width: 300px;

  .memo-label {
    color: ${COLOR_SYSTEM.get("Gray")?.[500]};
    font-weight: 500;
    font-size: 12px;
  }

  .memo-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
  }

  .edit-button {
    padding: 2px 6px;
    font-size: 11px;
    color: ${COLOR_SYSTEM.get("Skyblue")?.[600]};
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${COLOR_SYSTEM.get("Skyblue")?.[700]};
    }
  }
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  box-shadow: 0 1px 4px 0 rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
`;

const ToggleSwitch = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 6px;
  height: 30px;
`;
const CardHeader = styled.div`
  background: #fff;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;
const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
`;
const SectionHeader = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[20]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  flex-shrink: 0;
`;
const SectionHeaderContent = styled.div`
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 10px 0;
  height: 20px;
`;

const EditingIndicator = styled.span<{ $isSaving?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  font-size: 12px;
  font-weight: 400;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
    animation: ${props => props.$isSaving ? 'pulse 1.5s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
const SectionBody = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  background: #fff;
  overflow-y: auto;
  flex-grow: 1;
`;

const PatientInfoSection = styled.div`
  overflow-y: auto;
  padding: 16px;
  height: 100%;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLOR_SYSTEM.get("Gray")?.[300]};
    border-radius: 3px;

    &:hover {
      background: ${COLOR_SYSTEM.get("Gray")?.[400]};
    }
  }
`;
const InfoLabel = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-size: 14px;
  margin-bottom: 4px;
`;
const InfoBox = styled.div`
  background: #fff;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin-bottom: 0;
`;

const ReviewContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 120px;
  padding: 4px 0;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }

  &.danger {
    color: ${COLOR_SYSTEM.get("Red")?.[600]};
  }

  &.danger:hover {
    background-color: ${COLOR_SYSTEM.get("Red")?.[50]};
  }
`;

const AIReviewNotice = styled.div`
  padding: 8px 16px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;

  .edit-link {
    color: ${COLOR_SYSTEM.get("Blue")?.[500]};
    cursor: pointer;
    text-decoration: none;
    margin-left: 4px;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
      color: ${COLOR_SYSTEM.get("Blue")?.[600]};
    }
  }
`;

/** 상담 체크리스트 버튼 **안**의 흐린 작은 꼬리표 "작성됨"(결정 20, 사용자 지시 2026-10-13 — 버튼 밖 형제가 아니라 글자 뒤에 붙인다). */
const TextRecordWrittenTag = styled.span`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 400;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  white-space: nowrap;
`;

const FullscreenButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 16px;
  z-index: 10;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledPanelResizeHandle = styled(PanelResizeHandle)<{
  direction?: "horizontal" | "vertical";
}>`
  background: none !important;
  border: none !important;
  position: relative;
  outline: none;
  cursor: ${(props) => (props.direction === "vertical" ? "row-resize" : "col-resize")};

  &::before {
    content: "";
    position: absolute;
    ${(props) =>
      props.direction === "vertical"
        ? `
      left: 0;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 1px;
      width: 100%;
    `
        : `
      top: 0;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 1px;
      height: 100%;
    `}
    background-color: ${COLOR_SYSTEM.get("Gray")?.[300] ?? "#D1D5DB"};
    transition: all 0.2s ease-in-out;
  }

  &:hover::before {
    ${(props) => (props.direction === "vertical" ? "height: 5px;" : "width: 5px;")}
    background-color: ${COLOR_SYSTEM.get("Gray")?.[500] ?? "#6B7280"};
  }

  &[data-resize-handle-active]::before {
    ${(props) => (props.direction === "vertical" ? "height: 5px;" : "width: 5px;")}
    background-color: ${COLOR_SYSTEM.get("Gray")?.[700] ?? "#374151"};
  }
`;

export interface AIReviewDetailTemplateProps {
  review: AIReview;
  evaluation?: any; // AIReviewEvaluation 타입
  onAdminDetail?: () => void;
  onTogglePin?: () => void;
  onEditMemo?: () => void;
  onDeleteReport?: () => void;
  onReviewUpdate?: (updatedReview: AIReview) => void;
  message?: string;
  hideHeader?: boolean; // 헤더 숨김 옵션
  // 읽기 전용(파트너 열람 등) — 쓰기 UI를 렌더 차단한다. 기본 false = 기존 동작(계획 §3-2 · T3, additive).
  readOnly?: boolean;
  isCompareMode?: boolean; // 비교 모드 여부
  isFullscreen?: boolean; // 전체화면 모드
  onToggleFullscreen?: () => void; // 전체화면 토글 핸들러
  // 피드백 탭 상태 (Page에서 관리)
  activeFeedbackTab?: string;
  onFeedbackTabChange?: (tab: string | undefined) => void;
  // 스트림 끊김 상태 (Page의 useAIReviewStream이 관리)
  disconnection?: StreamDisconnection;
  /**
   * 성상·각인 소스(파트너 열람 전용, additive). **주어지면 자체 `/drugs/pills` fetch를 건너뛴다**(계획 D-9).
   * 미전달(약사 5화면 등 기존 소비자)이면 기존 fetch 경로가 그대로 돈다 — 회귀 없음.
   * 마운트 시점 1회만 반영, 변경 동기화 없음.
   */
  pillsData?: DrugSearchResult[];
  /**
   * 상담 체크리스트 버튼(spec 설계 결정 8·20). 주어질 때만 버튼을 그린다 — 약사 보고서 페이지만 넘긴다.
   * 열림/문구/유무 조회 상태는 Page가 관리한다.
   */
  onOpenTextRecord?: () => void;
  hasTextRecord?: boolean; // 기록 있음 → 버튼 오른쪽 꼬리표 "작성됨"(결정 20)
  textRecordStatus?: "loading" | "ready" | "error"; // 유무 조회 상태(조회 전 비활성, 실패 시 「다시 시도」)
  onRetryTextRecord?: () => void;
}


const LOCAL_STORAGE_KEY = "aiReviewDetailPanelLayout";

const AIReviewDetailTemplate: React.FC<AIReviewDetailTemplateProps> = ({
  review,
  evaluation,
  onAdminDetail,
  onTogglePin,
  onEditMemo,
  onDeleteReport,
  onReviewUpdate,
  message,
  hideHeader = false,
  readOnly = false,
  isCompareMode = false,
  isFullscreen = false,
  onToggleFullscreen,
  activeFeedbackTab,
  onFeedbackTabChange,
  disconnection,
  // `:473`에 같은 이름의 state가 있어 `pillsDataProp`으로 별칭한다(재선언 충돌 방지, 계획 작업 10).
  pillsData: pillsDataProp,
  onOpenTextRecord,
  hasTextRecord = false,
  textRecordStatus = "ready",
  onRetryTextRecord,
}) => {
  const [selectedReferenceId, setSelectedReferenceId] = useState<string | undefined>(undefined);
  const [isShowingEdited, setIsShowingEdited] = useState<boolean>(true); // 수정본 우선 표시
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [prescriptionViewMode, setPrescriptionViewMode] = useState<"date" | "hospital">("date");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [highlightedItemCode, setHighlightedItemCode] = useState<string | null>(null);
  // 파랑 하이라이트 단일 축(spec 설계 결정 7·11) — 병원(그 병원 그룹 전부) 또는 처방전(그룹 하나).
  // 약물 하이라이트(초록)와 상호 배타. 병원별 보기는 렌더 순서≠스냅샷 순서라 groupId 정체성으로 매칭.
  const [blueHighlight, setBlueHighlight] = useState<
    { kind: "hospital"; alias: string } | { kind: "prescription"; groupId: string } | null
  >(null);
  const [isReferenceInfoModalOpen, setIsReferenceInfoModalOpen] = useState<boolean>(false);
  const [historyViewerOpen, setHistoryViewerOpen] = useState<boolean>(false);
  // prop이 주어지면(파트너 열람) 그 값으로 시작하고 아래 effect의 자체 fetch는 건너뛴다(D-9). 미전달이면 [].
  const [pillsData, setPillsData] = useState<DrugSearchResult[]>(pillsDataProp ?? []);
  const editorRef = useRef<any>(null);
  const reviewSectorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // 좌측 처방 패널 imperative 핸들(접힘 시 expand) + 처방 스크롤 컨테이너(그룹으로 직접 스크롤).
  const leftPanelRef = useRef<ImperativePanelHandle>(null);
  const prescriptionScrollRef = useRef<HTMLDivElement>(null);

  // AI 검토안 본문(reviewSectorRef)의 마크다운 헤더로 목차 생성.
  // 비동기 fetch/스트리밍으로 본문이 나중에 채워지거나 원본/수정본 토글·편집·상태가
  // 바뀌면 재수집한다. (콘텐츠 문자열을 deps에 넣어 DOM 갱신 시 stale element를 방지)
  const { items: tocItems, scrollToItem: scrollToTocItem } = useReviewToc(
    reviewSectorRef,
    [
      review.id,
      review.reviewResult,
      review.reviewResultEdited,
      review.status,
      isShowingEdited,
      isEditMode,
    ]
  );

  // 현재 사용자 정보
  const currentUser = useSelector((state: RootState) => state.user.user);

  // 편집 권한 체크 (작성자 본인만)
  const canEdit = currentUser && review.userId === currentUser.id;

  // 수정본 존재 여부
  const hasEditedVersion = !!review.reviewResultEdited;

  // 분석 진행 중 상태 확인
  const isProcessing =
    review.status &&
    review.status >= AIReviewStatus.PROCESSING &&
    review.status < AIReviewStatus.COMPLETED;

  const [panelLayout, setPanelLayout] = useState<number[] | undefined>(() => {
    const storedLayout = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedLayout) {
      try {
        const parsedLayout = JSON.parse(storedLayout);
        if (
          Array.isArray(parsedLayout) &&
          parsedLayout.length === 3 &&
          parsedLayout.every((num) => typeof num === "number")
        ) {
          return parsedLayout;
        }
      } catch (e) {
        log(LogLevel.ERROR, "Error parsing stored panel layout", e);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
    return undefined;
  });
  const handleReferenceClick = (refId: string) => {
    log(LogLevel.INFO, "Reference clicked", { refId });
    eventLog("근거자료링크선택", { refId: refId, reviewId: review.id });
    // 같은 ref를 다시 클릭해도 항상 하이라이트가 재적용되도록 — 목차 점프 등으로 하이라이트가
    // 다른 위치로 옮겨간 뒤 같은 ref를 눌렀을 때 effect가 재실행되지 않는 문제 방지.
    setSelectedReferenceId(undefined);
    requestAnimationFrame(() => setSelectedReferenceId(refId));
  };

  // 의약품 + 건기식 통합 — 약사 화면의 성상 fetch(itemCode) 소스이자 이름 소스. deps·내용 불변(회귀 기준선).
  const baseDrugsData = useMemo(() => [
    ...(review.referenceSnap?.drugs || []),
    ...(review.referenceSnap?.hff || []),
  ], [review.referenceSnap?.drugs, review.referenceSnap?.hff]);

  // 이름 소스 drugsData — 약사 경로(pillsDataProp 미전달)는 baseDrugsData를 **동일 참조로** 반환해 fetch·렌더가
  // 바이트 동일하다(P-1). 파트너 경로(pillsDataProp 전달)만 성상(pills) 조인 집합과 같은 범위로 이름을 넓힌다
  // (D-18 대칭 · P-3·P-4) — patientSnap(처방·OTC·건기식·식별의약품) + pills.itemName 폴백. fetch는 baseDrugsData가
  // 담당하므로(아래 effect) 이 위젯 배열은 **이름 조회 전용**이고 약사 fetch를 오염시키지 않는다.
  const drugsData = useMemo(() => {
    if (pillsDataProp === undefined) return baseDrugsData;
    return [
      ...baseDrugsData,
      ...collectSnapDrugs(review.patientSnap),
      ...pillsNameFallback(pillsDataProp),
    ];
  }, [baseDrugsData, pillsDataProp, review.patientSnap]);

  // baseDrugsData의 itemCodes로 성상(pillsData) 조회. itemCode 소스는 **baseDrugsData**다(위젯 drugsData 아님)
  // — 파트너 경로가 넓힌 이름 소스가 약사 fetch 페이로드를 오염시키지 않게 구조 분리한다(P-1).
  useEffect(() => {
    // 파트너 열람(pillsData prop 전달)은 성상을 이미 받았으므로 자체 fetch를 건너뛴다(D-9). 여기서 401이
    // 나가던 파트너 독립뷰 요청 2회도 함께 사라진다. 약사 5화면은 prop 미전달이라 아래 fetch가 그대로 돈다.
    if (pillsDataProp !== undefined) return;

    const fetchPillsData = async () => {
      if (baseDrugsData.length === 0) {
        setPillsData([]);
        return;
      }

      const itemCodes = baseDrugsData.map(d => d.itemCode).filter(Boolean);
      if (itemCodes.length === 0) {
        setPillsData([]);
        return;
      }

      try {
        const result = await getPillsByItemCodes(itemCodes);
        setPillsData(result);
        log(LogLevel.INFO, "pillsData 조회 완료", { count: result.length });
      } catch (error) {
        log(LogLevel.ERROR, "pillsData 조회 실패", error);
        setPillsData([]);
      }
    };

    fetchPillsData();
  }, [baseDrugsData, pillsDataProp]);

  // EvidenceSection에서 제공하는 scrollToMedication 함수를 저장할 ref
  const scrollToMedicationRef = useRef<((itemCode: string) => void) | null>(null);

  const handleMedicationClick = (prescription: Prescription) => {
    log(LogLevel.INFO, "Medication clicked", {
      itemCode: prescription.itemCode,
      medicationName: prescription.medicationName,
    });

    const medicationRefId = `medication-${prescription.itemCode}`;
    setSelectedReferenceId(medicationRefId);
    eventLog("처방전 약물 클릭", {
      reviewId: review.id,
      itemCode: prescription.itemCode,
      medicationName: prescription.medicationName,
    });

    // 허가사항 탭의 해당 약물로 스크롤
    if (scrollToMedicationRef.current && prescription.itemCode) {      scrollToMedicationRef.current(prescription.itemCode);
    }
  };

  const handleDrugImageClick = (itemCode: string) => {
    log(LogLevel.INFO, "DrugImage clicked", {
      itemCode,
    });

    const medicationRefId = `medication-${itemCode}`;
    setSelectedReferenceId(medicationRefId);
    eventLog("약품 이미지 클릭", {
      reviewId: review.id,
      itemCode,
    });

    // 허가사항 탭의 해당 약물로 스크롤
    if (scrollToMedicationRef.current) {      scrollToMedicationRef.current(itemCode);
    }
  };

  const patient = review.patientSnap;
  // 분석 시점 박제: 평탄(prescriptions) 우선, 없으면 그룹(prescriptionGroups) flatten.
  // useMemo로 안정화 — 매 렌더 새 배열이면 타임라인이 매번 재집계됨
  const snapPrescriptions = useMemo(
    () =>
      patient?.prescriptions?.length
        ? patient.prescriptions
        : patient?.prescriptionGroups?.flatMap((g) => g.prescriptions ?? []) ?? [],
    [patient]
  );

  // 병원 alias→{name,label} 맵 — 3단 본문의 병원 마커 복원·버튼 label·인쇄 실명 복원 재료.
  const hospitalAliasMap = useMemo(
    () => buildHospitalAliasMapFromGroups(patient?.prescriptionGroups),
    [patient]
  );

  // 단일 파랑 축에서 파생 — 좌측 처방 패널 강조 전용(본문 마커는 선택 상태를 표시하지 않음, 설계 결정 13).
  //   병원: 그 병원 그룹 전부(alias) / 처방전: 그 그룹 하나(groupId).
  const highlightedHospital = blueHighlight?.kind === "hospital" ? blueHighlight.alias : null;
  const highlightedGroupId = blueHighlight?.kind === "prescription" ? blueHighlight.groupId : null;

  // 병원 버튼 클릭 → 그 병원 그룹 전부 하이라이트(토글). 약물 하이라이트(초록)와 상호 배타.
  const handleHospitalClick = useCallback((alias: string) => {
    const norm = alias.toUpperCase();
    setHighlightedItemCode(null); // 상호 배타: 약물 하이라이트 해제
    setBlueHighlight((prev) =>
      prev?.kind === "hospital" && prev.alias === norm ? null : { kind: "hospital", alias: norm }
    );
    eventLog("보고서_병원버튼_클릭", { reviewId: review.id, alias: norm });
  }, [review.id]);

  // 처방전 버튼 클릭 → 그 그룹 하나 하이라이트(토글). N은 스냅샷 1-기반 인덱스, groupId로 정체성 매칭.
  const handlePrescriptionClick = useCallback((n: number) => {
    const groups = patient?.prescriptionGroups ?? [];
    const gid = groups[n - 1]?.groupId;
    if (!gid) return; // 화이트리스트 밖/groupId 부재 → 무동작
    setHighlightedItemCode(null); // 상호 배타
    setBlueHighlight((prev) =>
      prev?.kind === "prescription" && prev.groupId === gid
        ? null
        : { kind: "prescription", groupId: gid }
    );
    eventLog("보고서_처방전버튼_클릭", { reviewId: review.id, n });
  }, [review.id, patient]);

  // 약물 하이라이트 설정 시 파랑 하이라이트 해제(상호 배타). EvidenceSection에 넘긴다.
  const handleSetHighlightedItemCode = useCallback((code: string | null) => {
    setHighlightedItemCode(code);
    if (code) setBlueHighlight(null);
  }, []);

  // 파랑 하이라이트 시 좌측 패널 expand(접혀 있으면 편다·편 상태 유지) + 대상으로 컨테이너 직접 스크롤.
  // 병원=첫 매칭 그룹(data-hospital-alias) / 처방전=그 그룹(data-group-id, 위치 아닌 정체성).
  // scrollIntoView 금지(중첩 스크롤에서 엉뚱한 조상 스크롤) — useReviewToc.ts 패턴 재사용.
  useEffect(() => {
    if (!blueHighlight) return;
    leftPanelRef.current?.expand();
    const selector =
      blueHighlight.kind === "hospital"
        ? `[data-hospital-alias="${blueHighlight.alias}"]`
        : `[data-group-id="${blueHighlight.groupId}"]`;
    const rafId = window.requestAnimationFrame(() => {
      const container = prescriptionScrollRef.current;
      if (!container) return; // in-memory 모드 등 처방 테이블 부재 → 무동작(허용)
      const el = container.querySelector<HTMLElement>(selector);
      if (!el || !el.isConnected) return;
      const top = Math.max(
        0,
        container.scrollTop +
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          SCROLL_TOP_GAP,
      );
      container.scrollTo({ top, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [blueHighlight]);

  // 패널 레이아웃 저장 — react-resizable-panels의 onLayout은 리사이즈 중 매 프레임 호출되므로,
  // 동기 localStorage 쓰기를 디바운스해 리사이즈 잰크를 줄인다. (autoSaveId와 키만 다른 수동 저장)
  const handleLayout = useMemo(
    () =>
      debounce((sizes: number[]) => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sizes));
        log(LogLevel.VERBOSE, "Panel layout changed and saved", sizes);
      }, 200),
    []
  );
  // 언마운트 시 대기 중인 저장은 버리지 말고 flush — 직전 레이아웃 변경 누락 방지
  useEffect(() => () => handleLayout.flush(), [handleLayout]);

  const age = patient?.age || calculateAge(patient?.birthDate);

  const handleToggleFavorite = async () => {
    // TODO: API 구현 후 연동
    log(LogLevel.INFO, "즐겨찾기 토글", { reviewId: review.id });
    // await toggleAIReviewFavorite(review.id, !isFavorite);
    onTogglePin?.();
  };

  const handleEditMemo = () => {
    onEditMemo?.();
  };

  // 하단 피드백 배지 클릭 시 피드백 탭 활성화
  const handleActivateFeedbackTab = () => {
    onFeedbackTabChange?.('피드백');
    eventLog("ai_review_feedback_tab_activated", { reviewId: review.id });
  };

  // 편집 내용 변경 핸들러
  const handleContentChange = (newContent: string) => {
    log(LogLevel.VERBOSE, "[편집] 콘텐츠 변경", {
      contentLength: newContent.length,
      previousLength: editedContent.length,
    });

    setEditedContent(newContent);
  };

  const handleDeleteReport = () => {
    onDeleteReport?.();
  };

  // 인쇄 버튼 클릭 핸들러
  // 편집 내용 저장 함수를 먼저 선언
  const handleSaveEdit = async (): Promise<void> => {
    try {
      setIsSaving(true);

      // Base64 이미지를 업로드하고 URL로 변경
      let processedContent: string;
      try {
        processedContent = await processBase64Images(editedContent);
      } catch (imageError) {
        console.error("이미지 업로드 실패:", imageError);
        ToastManager.show({
          content: "이미지 업로드에 실패했습니다. 다시 시도해주세요.",
          duration: 3000,
        });
        setIsSaving(false);
        return; // 편집 모드 유지
      }

      // API 호출하여 HTML 형태로 저장
      const updatedReview = await updateAIReviewEdited(review.id, processedContent);

      // 상위 컴포넌트에 업데이트된 리뷰 전달
      if (onReviewUpdate) {
        onReviewUpdate(updatedReview);
      }

      // 저장 성공 시에만 편집 모드 종료
      setIsEditMode(false);
      setIsShowingEdited(true); // 저장 후 수정본 표시
      setIsSaving(false); // 저장 상태 초기화
      // 편집 모드 종료되면 전역 변수도 자동으로 false로 설정됨 (useEffect에서 처리)

      ToastManager.show({
        content: "수정 내용이 저장되었습니다.",
        duration: 2000,
      });
      setEditedContent(""); // 편집 콘텐츠 초기화

      eventLog("AI리뷰_편집_저장", { reviewId: review.id });
    } catch (error) {
      console.error("편집 저장 실패:", error);
      ToastManager.show({
        content: "저장에 실패했습니다. 다시 시도해주세요.",
        duration: 3000,
      });
      setIsSaving(false);
      // 편집 모드 유지 - setIsEditMode(false)를 호출하지 않음
    }
  };

  // 편집 모드 상태를 전역 변수에 저장
  useEffect(() => {
    log(LogLevel.INFO, "[AIReviewDetailTemplate] 편집 모드 상태 변경", {
      isEditMode,
      reviewId: review.id,
    });

    // 전역 변수에 편집 모드 상태 저장
    (window as any).__isInEditMode = isEditMode;

    if (!isEditMode) {
      log(LogLevel.INFO, "[AIReviewDetailTemplate] 편집모드 종료");
    }
  }, [isEditMode, review.id]);
  
  // review.id 변경 시 편집 모드 자동 종료 및 모든 상태 초기화
  useEffect(() => {
    log(LogLevel.INFO, "[AIReviewDetailTemplate] Review ID 변경 감지", {
      reviewId: review.id,
      feedbackId: review.feedbackId,
      currentEditMode: isEditMode,
      currentHighlightedItemCode: highlightedItemCode,
    });

    // 편집 모드였다면 종료
    if (isEditMode) {
      log(LogLevel.INFO, "[AIReviewDetailTemplate] Review 변경으로 편집 모드 자동 종료");
      setIsEditMode(false);
      setEditedContent("");
    }

    // 하이라이트 초기화 (약물 초록 · 파랑 축 모두)
    setHighlightedItemCode(null);
    setBlueHighlight(null);

    // 피드백 탭 초기화 (피드백 상태는 FeedbackContext에서 관리)
    onFeedbackTabChange?.(undefined);
  }, [review.id]);

  // 컴포넌트 언마운트 시 전역 변수 정리
  useEffect(() => {
    log(LogLevel.INFO, "[AIReviewDetailTemplate] 컴포넌트 마운트", {
      reviewId: review.id,
    });

    return () => {
      log(LogLevel.INFO, "[AIReviewDetailTemplate] 컴포넌트 언마운트, 전역 변수 정리", {});
      (window as any).__isInEditMode = false;
    };
  }, []);

  // 네비게이션 차단 훅 사용 (브라우저 새로고침/탭 닫기만)
  useNavigationBlocker({
    when: isEditMode,
  });

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // ESC 키 핸들러 (드롭다운 닫기 + 전체화면 해제)
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // 우선순위: 드롭다운 > 전체화면
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
        } else if (isFullscreen && onToggleFullscreen) {
          onToggleFullscreen();
        }
      }
    };

    if (isDropdownOpen || isFullscreen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isDropdownOpen, isFullscreen, onToggleFullscreen]);

  const handlePrint = () => {
    // SessionStorage에 리뷰 데이터 저장
    sessionStorage.setItem("print_review_latest", JSON.stringify(review));

    // 새 창으로 인쇄 페이지 열기
    const printWindow = window.open(
      `/ai-reviews/${review.id}/print`,
      "print_preview",
      "width=1200,height=800,menubar=no,toolbar=no,location=no,status=no"
    );

    if (!printWindow) {
      window.alert("팝업 차단을 해제하고 다시 시도해주세요.");
    }

    // 이벤트 로그
    eventLog("AI리뷰_인쇄_버튼_클릭", { reviewId: review.id });
  };

  // 편집 모드 진입
  const handleEnterEditMode = () => {
    log(LogLevel.INFO, "[AIReviewDetailTemplate] 편집 모드 진입", {
      reviewId: review.id,
      hasEditedVersion,
    });
    let htmlContent: string;

    if (hasEditedVersion) {
      // 수정본이 있으면 무조건 수정본을 편집 (원본/수정본 토글과 무관)
      // 수정본은 이미 HTML 형태로 저장되어 있음
      htmlContent = review.reviewResultEdited;
    } else {
      // 수정본이 없는 경우 - 현재 화면에 렌더링된 HTML을 추출
      if (reviewSectorRef.current) {
        // ReviewSection wrapper를 제외한 내부 콘텐츠만 추출
        const reviewSectionBody = reviewSectorRef.current.querySelector(
          '[class*="ReviewSectionBody"]'
        );
        const contentElement = reviewSectionBody || reviewSectorRef.current;

        // DOM을 복제하여 버튼 요소 처리
        const clonedElement = contentElement.cloneNode(true) as HTMLElement;

        // data-exclude-from-save 속성이 있는 요소 제거 (피드백 컴포넌트 등)
        const excludeElements = clonedElement.querySelectorAll('[data-exclude-from-save="true"]');
        excludeElements.forEach((el) => el.remove());

        // 모든 code 태그를 찾아서 처리 (약 이름 등)
        const allCodeTags = clonedElement.querySelectorAll("code");
        allCodeTags.forEach((codeTag) => {
          // code 태그를 span으로 변환하되, 스타일은 유지
          const spanElement = document.createElement("span");
          spanElement.style.fontFamily = "'Consolas', 'Monaco', monospace";
          spanElement.style.fontSize = "0.9em";
          spanElement.style.padding = "2px 4px";
          spanElement.style.borderRadius = "3px";
          spanElement.style.backgroundColor = "#f5f5f5"; // 기본 배경색
          spanElement.textContent = codeTag.textContent || "";

          // code 태그를 span으로 교체
          codeTag.parentNode?.replaceChild(spanElement, codeTag);
        });

        // 모든 참고문헌 버튼을 찾아서 Reference 노드로 인식할 수 있는 형태로 변환
        const allButtons = clonedElement.querySelectorAll(
          "button[data-reference-id][data-reference-link]"
        );

        allButtons.forEach((button) => {
          const refId = button.getAttribute("data-reference-id");
          const refLink = button.getAttribute("data-reference-link");

          if (refId && refLink) {
            // 버튼을 Reference 노드가 인식할 수 있는 span으로 변환
            const spanElement = document.createElement("span");
            spanElement.setAttribute("data-reference-id", refId);
            spanElement.setAttribute("data-reference-link", "true");
            spanElement.textContent = button.textContent || "";

            // 버튼을 span으로 교체
            button.parentNode?.replaceChild(spanElement, button);
          }
        });

        // 병원 버튼을 span[data-hospital-alias][data-hospital-link]으로 변환(alias 보존 · 편집 왕복 규약).
        // 없으면 실제 병원명만 남고 alias가 소실돼 첫 저장에서 재변환이 불가능해진다.
        convertHospitalButtonsToSpans(clonedElement);

        // 처방전 버튼은 평문 텍스트로 되돌린다(텍스트가 식별자라 재렌더에서 재버튼화 — 설계 결정 11).
        unwrapPrescriptionButtons(clonedElement);

        // DrugImage 컴포넌트를 단순 img 태그로 변환
        const drugContainers = clonedElement.querySelectorAll('[data-drug-container="true"]');
        drugContainers.forEach((container) => {
          const img = container.querySelector('img');
          const itemCode = container.getAttribute('data-itemcode');

          if (itemCode) {
            const src = img?.getAttribute('src') || '';

            // 단순 img 태그로 교체 (이미지 없어도 data-itemcode 보존)
            const simpleImg = document.createElement('img');
            if (src) {
              simpleImg.setAttribute('src', src);
            }
            simpleImg.setAttribute('data-itemcode', itemCode);
            simpleImg.setAttribute('alt', '약품이미지');

            // DrugImage 컨테이너를 img로 교체
            container.parentNode?.replaceChild(simpleImg, container);
          }
        });

        htmlContent = clonedElement.innerHTML;
      } else {
        // ref가 없는 경우 기본값 사용
        console.warn("편집 시작 시 ReviewSector ref를 찾을 수 없습니다.");
        htmlContent = "<p>편집할 콘텐츠를 불러올 수 없습니다.</p>";
      }
    }

    setEditedContent(htmlContent);
    setIsEditMode(true);
    log(LogLevel.INFO, "[AIReviewDetailTemplate] 편집 모드 진입 완료", {
      htmlContentLength: htmlContent.length,
    });

    eventLog("AI리뷰_편집모드_진입", { reviewId: review.id });
  };

  // 편집 내용 저장 함수는 위에서 이미 선언됨

  // 편집 취소
  const handleCancelEdit = () => {
    const confirmResult = window.confirm(
      "편집을 취소하시겠습니까?\n\n작성 중인 내용은 저장되지 않습니다."
    );

    if (!confirmResult) {
      log(LogLevel.INFO, "[AIReviewDetailTemplate] 편집 계속");
      return;
    }

    // 편집 취소 실행
    setIsEditMode(false);
    setEditedContent("");
    eventLog("AI리뷰_편집_취소", { reviewId: review.id });
  };

  // 편집 내용 삭제
  const handleDeleteEdit = async () => {
    setIsDropdownOpen(false); // 드롭다운 닫기
    
    const confirmResult = window.confirm(
      "수정된 내용을 모두 삭제하고 원본만 남기시겠습니까?\n\n이 작업은 되돌릴 수 없습니다."
    );

    if (!confirmResult) {
      log(LogLevel.INFO, "[AIReviewDetailTemplate] 편집 내용 삭제 취소");
      return;
    }

    try {
      setIsSaving(true);

      // 빈 문자열로 저장하여 편집 내용 삭제
      const updatedReview = await updateAIReviewEdited(review.id, "");

      // 상위 컴포넌트에 업데이트된 리뷰 전달
      if (onReviewUpdate) {
        onReviewUpdate(updatedReview);
      }

      // 편집 모드 종료
      setIsEditMode(false);
      setIsShowingEdited(false); // 원본으로 전환
      setEditedContent("");

      eventLog("AI리뷰_편집_삭제", { reviewId: review.id });
    } catch (error) {
      console.error("편집 내용 삭제 실패:", error);
      alert("삭제에 실패했습니다. 다시 시도해 주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  // Reference 삽입 함수
  const handleInsertReference = (referenceId: string) => {
    if (!editorRef.current) return;

    // 참조는 항상 "ref"로 표시
    const displayText = "ref";

    // Reference 노드로 인식할 수 있는 span HTML 생성
    const referenceHtml = `<span data-reference-id="${referenceId}" data-reference-link="true">${displayText}</span> `;
    editorRef.current.insertText(referenceHtml);
    eventLog("AI리뷰_편집_참조삽입", { reviewId: review.id, referenceId });
  };

  log(LogLevel.VERBOSE, "AIReviewDetailTemplate", review);

  // 상담 체크리스트 버튼(설계 결정 8·20, 화면 설계 1) — 보고서 제목 줄 오른쪽 끝(「전체화면」 왼쪽),
  // 전체화면 모드에서는 닫기 버튼 옆. 한 곳에서 정의해 두 자리에서 쓴다(두 자리는 isFullscreen으로 배타).
  // 타겟 높이·스타일은 같은 줄의 「전체화면」 버튼과 같은 Button atom 변형(size Small·Outlined, 높이 32px).
  // 문구는 「상담 체크리스트」 고정, 기록이 있으면 버튼 오른쪽에 흐린 꼬리표 "작성됨"(✔·개수 없음 — 결정 20).
  // 꼬리표는 버튼 글자 뒤에 붙인다(버튼 안).
  const textRecordButton =
    onOpenTextRecord &&
    review.status === AIReviewStatus.COMPLETED &&
    canEdit &&
    !readOnly &&
    !isCompareMode &&
    !isEditMode ? (
      <Button
        buttonType="button"
        type="Text"
        size="Small"
        color={textRecordStatus === "error" ? "Negative" : "Primary"}
        variant="Outlined"
        disabled={textRecordStatus === "loading"}
        onClick={textRecordStatus === "error" ? onRetryTextRecord : onOpenTextRecord}
        style={{
          // 좁은 창에서 버튼 글자가 잘리지 않게(결함 7). 버튼 atom은 overflow:hidden이라
          // 줄어들면 글자가 깎인다 — 안 줄고 안 접히게 둔다.
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {textRecordStatus === "error" ? (
          "다시 시도"
        ) : (
          <>
            상담 체크리스트
            {textRecordStatus === "ready" && hasTextRecord && (
              <TextRecordWrittenTag data-testid="checklist-written-tag">작성됨</TextRecordWrittenTag>
            )}
          </>
        )}
      </Button>
    ) : null;

  return (
    <OuterContainer $isFullscreen={isFullscreen}>
      {/* 전체화면 모드일 때 우측 상단에 닫기 버튼 표시. 제목 줄이 사라지므로 상담 체크리스트 버튼(+꼬리표)을 닫기 옆에 둔다(화면 설계 1). */}
      {!isCompareMode && onToggleFullscreen && isFullscreen && (
        <FullscreenButtonContainer>
          {textRecordButton}
          <Button
            type="IconWithText"
            size="Small"
            color="Secondary"
            variant="Ghost"
            icon="Full Screen Exit"
            onClick={onToggleFullscreen}
            left
          >
            전체화면 닫기(Esc)
          </Button>
        </FullscreenButtonContainer>
      )}
      {!hideHeader && !isFullscreen && (
        <TopHeader>
          <HeaderContent>
            <div style={{ width: "100%" }}>
              <Flex alignItems="center" gap="8px">
                <Text textType="Headline" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                  보고서 #{review.id}
                </Text>
                {!isCompareMode && !readOnly && (
                  <ToggleButton
                    color="Yellow"
                    size="Medium"
                    type="Icon"
                    variant="Ghost"
                    icon="Pin 02 Filled"
                    active={!!review.pinnedAt}
                    onClick={handleToggleFavorite}
                  />
                )}
              </Flex>
              {!readOnly && (
                <Flex alignItems="center" gap="4px" style={{ marginTop: 2, width: "100%" }}>
                  {review.memo && (
                    <InfoIconButton
                      tooltip={<div style={{ whiteSpace: "pre-wrap" }}>{review.memo}</div>}
                      iconSize={14}
                      iconColor={COLOR_SYSTEM.get("Skyblue")?.[500]}
                      tooltipPosition="bottom"
                      tooltipMaxWidth="800px"
                    />
                  )}
                  <Text
                    textType="Caption"
                    color={COLOR_SYSTEM.get("Skyblue")?.[500]}
                    style={{
                      cursor: "pointer",
                      fontSize: "11px",
                      maxWidth: "60%",
                    }}
                    onClick={handleEditMemo}
                    numberOfLines={1}
                  >
                    메모 : {review.memo || "클릭하여 보고서에 대한 메모를 입력하세요"}
                  </Text>
                </Flex>
              )}
            </div>
          </HeaderContent>
          {!isCompareMode && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {/* 제목 줄 오른쪽 끝, 「전체화면」 왼쪽(화면 설계 1) */}
              {textRecordButton}
              {onToggleFullscreen && (
                <Button
                  type="IconWithText"
                  size="Small"
                  color="Secondary"
                  variant="Outlined"
                  icon="Full Screen"
                  onClick={onToggleFullscreen}
                  left
                >
                  전체화면
                </Button>
              )}
            </div>
          )}
        </TopHeader>
      )}
      <Card>
        <PanelGroup
          direction="horizontal"
          onLayout={handleLayout}
          autoSaveId={LOCAL_STORAGE_KEY}
          style={{ flex: 1, overflow: "hidden" }}
        >
          <Panel ref={leftPanelRef} defaultSize={panelLayout ? panelLayout[0] : 30} minSize={20} collapsible={true}>
            <PanelContent>
              <SectionHeader>
                <SectionHeaderContent>
                  <SectionTitle>환자 및 처방전 정보</SectionTitle>
                </SectionHeaderContent>
              </SectionHeader>
              <SectionBody
                style={{
                  borderBottom: "none",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: "0",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <PanelGroup direction="vertical" style={{ height: "100%" }}>
                  <Panel defaultSize={40} minSize={20} collapsible={false}>
                    <div style={{ 
                      padding: "16px",
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden"
                    }}>
                      {patient ? (
                        <PatientInfoDisplay
                          patient={patient}
                          age={age}
                          promptType={review.promptType}
                        />
                      ) : readOnly ? (
                        // 파트너 열람: 환자 개인정보를 주지 않는다(계약 §6). 로딩 오해를 막는 중립 문구.
                        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                          환자 정보는 제공되지 않습니다.
                        </Text>
                      ) : (
                        <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                          {message || "데이터를 불러오는 중입니다..."}
                        </Text>
                      )}
                    </div>
                  </Panel>

                  <StyledPanelResizeHandle direction="vertical" />

                  <Panel defaultSize={60} minSize={30} collapsible={false}>
                    <div
                      ref={prescriptionScrollRef}
                      style={{
                        padding: "16px",
                        height: "100%",
                        overflowY: "auto",
                        position: "relative",
                      }}
                    >
                      {/* 우상단 버튼 묶음 — 좌상단(TableTitle+입력소스 아이콘)을 가리지 않도록
                          두 버튼을 우상단 flex 컨테이너로 모은다. 표시 조건이 서로 달라
                          (처방이력=snapPrescriptions / 보기전환=prescriptionGroups) 컨테이너는
                          하나라도 참일 때 렌더하고, 안에서 각 버튼을 개별 조건으로 감싼다. */}
                      {(snapPrescriptions.length > 0 ||
                        (patient?.prescriptionGroups?.length ?? 0) > 0) && (
                        <div
                          style={{
                            position: "absolute",
                            top: "16px",
                            right: "16px",
                            zIndex: 10,
                            display: "flex",
                            gap: "8px",
                          }}
                        >
                          {snapPrescriptions.length > 0 && (
                            <Button
                              size="XSmall"
                              type="IconWithText"
                              variant="Outlined"
                              color="Secondary"
                              icon="Eye"
                              left
                              onClick={() => setHistoryViewerOpen(true)}
                              style={{
                                backgroundColor: "white",
                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                              }}
                            >
                              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                                처방이력 파악하기
                              </Text>
                            </Button>
                          )}
                          {patient?.prescriptionGroups &&
                            patient.prescriptionGroups.length > 0 && (
                              <Button
                                size="XSmall"
                                type="IconWithText"
                                variant="Outlined"
                                color="Secondary"
                                icon="Swap Vertical"
                                left
                                onClick={() =>
                                  setPrescriptionViewMode((prev) =>
                                    prev === "date" ? "hospital" : "date"
                                  )
                                }
                                style={{
                                  backgroundColor: "white",
                                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                }}
                              >
                                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                                  {prescriptionViewMode === "hospital" ? "병원별 보기" : "날짜별 보기"}
                                </Text>
                              </Button>
                            )}
                        </div>
                      )}
                      {patient && (
                        <PrescriptionTableDisplay
                          prescriptionGroups={patient.prescriptionGroups || []}
                          otc={patient.otc || []}
                          supplements={patient.hff || []}
                          onMedicationClick={handleMedicationClick}
                          viewMode={prescriptionViewMode}
                          highlightedItemCode={highlightedItemCode}
                          highlightedHospital={highlightedHospital}
                          highlightedGroupId={highlightedGroupId}
                        />
                      )}
                    </div>
                  </Panel>
                </PanelGroup>
              </SectionBody>
            </PanelContent>
          </Panel>
          <StyledPanelResizeHandle direction="horizontal" />
          <Panel defaultSize={panelLayout ? panelLayout[1] : 30} minSize={20} collapsible={true}>
            <PanelContent>
              <SectionHeader>
                <SectionHeaderContent>
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <SectionTitle>
                      AI 검토안
                      {isEditMode && (
                        <EditingIndicator $isSaving={isSaving}>
                          {isSaving ? "저장 중..." : "수정 중"}
                        </EditingIndicator>
                      )}
                    </SectionTitle>
                    {!isEditMode && review.status === AIReviewStatus.COMPLETED && (
                      <TocDropdown items={tocItems} onSelect={scrollToTocItem} />
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {/* 상담 체크리스트 버튼은 보고서 제목 줄로 이동(화면 설계 1) — 여기(AI 검토안 머리)에서는 제거. */}
                    {!readOnly && hasEditedVersion && !isEditMode && (
                      <ToggleSwitch>
                        <ToggleButton
                          color="Blue"
                          size="Small"
                          variant="Ghost"
                          onClick={() => setIsShowingEdited(false)}
                          active={!isShowingEdited}
                          style={{
                            borderRadius: "4px",
                            transition: "all 0.2s ease",
                            backgroundColor: !isShowingEdited
                              ? COLOR_SYSTEM.get("Blue")?.[500]
                              : "transparent",
                            color: !isShowingEdited ? "white" : COLOR_SYSTEM.get("Gray")?.[700],
                            padding: "4px 10px",
                            fontSize: "12px",
                            height: "28px",
                            minHeight: "28px",
                          }}
                        >
                          원본
                        </ToggleButton>
                        <ToggleButton
                          color="Blue"
                          size="Small"
                          variant="Ghost"
                          onClick={() => setIsShowingEdited(true)}
                          active={isShowingEdited}
                          style={{
                            borderRadius: "4px",
                            transition: "all 0.2s ease",
                            backgroundColor: isShowingEdited
                              ? COLOR_SYSTEM.get("Blue")?.[500]
                              : "transparent",
                            color: isShowingEdited ? "white" : COLOR_SYSTEM.get("Gray")?.[700],
                            padding: "4px 10px",
                            fontSize: "12px",
                            height: "28px",
                            minHeight: "28px",
                          }}
                        >
                          수정본
                        </ToggleButton>
                        {canEdit && !isProcessing && (
                          <Button
                            type="Icon"
                            size="Small"
                            color="Primary"
                            variant="Ghost"
                            icon="Edit 01"
                            onClick={handleEnterEditMode}
                            disabled={!isShowingEdited}
                            style={{
                              opacity: isShowingEdited ? 1 : 0.4,
                              cursor: isShowingEdited ? "pointer" : "not-allowed",
                              height: "28px",
                              width: "28px",
                              minHeight: "28px",
                              minWidth: "28px",
                            }}
                          />
                        )}
                        {!isProcessing && (
                          <Button
                            type="Icon"
                            size="Small"
                            color="Primary"
                            variant="Ghost"
                            icon="Print"
                            onClick={handlePrint}
                            disabled={!isShowingEdited}
                            style={{
                              opacity: isShowingEdited ? 1 : 0.4,
                              cursor: isShowingEdited ? "pointer" : "not-allowed",
                              height: "28px",
                              width: "28px",
                              minHeight: "28px",
                              minWidth: "28px",
                            }}
                          />
                        )}
                        {canEdit && !isProcessing && (
                          <DropdownContainer ref={dropdownRef}>
                            <Button
                              type="Icon"
                              size="Small"
                              color="Secondary"
                              variant="Ghost"
                              icon="Dots Vertical"
                              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                              disabled={!isShowingEdited}
                              style={{
                                opacity: isShowingEdited ? 1 : 0.4,
                                cursor: isShowingEdited ? "pointer" : "not-allowed",
                                height: "28px",
                                width: "28px",
                                minHeight: "28px",
                                minWidth: "28px",
                                marginRight: "2px",
                              }}
                            />
                            {isDropdownOpen && isShowingEdited && (
                              <DropdownMenu>
                                <DropdownItem
                                  className="danger"
                                  onClick={handleDeleteEdit}
                                >
                                  <Icon
                                    name="Trash"
                                    size={14}
                                    color={COLOR_SYSTEM.get("Red")?.[600]}
                                  />
                                  수정본 삭제
                                </DropdownItem>
                              </DropdownMenu>
                            )}
                          </DropdownContainer>
                        )}
                      </ToggleSwitch>
                    )}
                    {isEditMode && (
                      <>
                        <Button
                          type="Text"
                          size="Small"
                          color="Tertiary"
                          variant="Ghost"
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                        >
                          취소
                        </Button>
                        <Button
                          type="Text"
                          size="Small"
                          color="Primary"
                          variant="Contained"
                          onClick={handleSaveEdit}
                          disabled={isSaving}
                        >
                          {isSaving ? "저장 중..." : "저장"}
                        </Button>
                      </>
                    )}
                  </div>
                </SectionHeaderContent>
              </SectionHeader>
              {!readOnly && !isEditMode && !hasEditedVersion && review.status === AIReviewStatus.COMPLETED && (
                <AIReviewNotice>
                  AI가 검토 초안을 준비했습니다. 편집하여 나만의 상담자료로 만든 후 인쇄해서 활용하세요.
                  {canEdit && (
                    <span
                      className="edit-link"
                      onClick={handleEnterEditMode}
                      role="button"
                      tabIndex={0}
                    >
                      수정 후 인쇄하기
                    </span>
                  )}
                </AIReviewNotice>
              )}
              <ReviewContentContainer ref={reviewSectorRef}>
                <ReviewSection
                  reviewId={review.id}
                  reviewResult={review.reviewResult}
                  reviewResultEdited={review.reviewResultEdited}
                  status={review.status}
                  message={review.processingMessage || message || review.reviewResult}
                  llmFailureCode={review.llmFailureCode}
                  llmFailureDetail={review.analysis?.llmFailureDetail}
                  llmFailureDetailAt={review.analysis?.llmFailureDetailAt}
                  disconnection={disconnection}
                  promptType={review.promptType}
                  isEditMode={isEditMode}
                  editedContent={editedContent}
                  showEditedVersion={isShowingEdited}
                  onEnterEditMode={handleEnterEditMode}
                  onExitEditMode={handleCancelEdit}
                  onContentChange={handleContentChange}
                  onSave={handleSaveEdit}
                  onDelete={handleDeleteEdit}
                  onReferenceClick={handleReferenceClick}
                  onDrugImageClick={handleDrugImageClick}
                  onActivateFeedbackTab={handleActivateFeedbackTab}
                  isSaving={isSaving}
                  hasEditedVersion={hasEditedVersion}
                  editorRef={editorRef}
                  drugsData={drugsData}
                  pillsData={pillsData}
                  hospitalAliasMap={hospitalAliasMap}
                  onHospitalClick={handleHospitalClick}
                  onPrescriptionClick={handlePrescriptionClick}
                  prescriptionGroupCount={patient?.prescriptionGroups?.length ?? 0}
                />
              </ReviewContentContainer>
            </PanelContent>
          </Panel>
          <StyledPanelResizeHandle direction="horizontal" />
          <Panel defaultSize={panelLayout ? panelLayout[2] : 40} minSize={20} collapsible={true}>
            <PanelContent>
              <SectionHeader>
                <SectionHeaderContent>
                  <SectionTitle>
                    근거자료
                    <InfoIconButton
                      iconName="Information Circle"
                      iconSize={16}
                      iconColor={COLOR_SYSTEM.get("Gray")?.[400]}
                      tooltip="근거자료 안내"
                      onClick={() => setIsReferenceInfoModalOpen(true)}
                    />
                  </SectionTitle>
                  {!readOnly && onAdminDetail && (
                    <Button
                      type="Icon"
                      variant="Ghost"
                      color="Primary"
                      size="Small"
                      icon="Arrow Right Filled"
                      onClick={onAdminDetail}
                    />
                  )}
                </SectionHeaderContent>
              </SectionHeader>
              <ReviewContentContainer>
                <EvidenceSection
                  referenceSnap={review.referenceSnap}
                  selectedReferenceId={selectedReferenceId}
                  message={message}
                  evaluation={evaluation}
                  isEditMode={isEditMode}
                  onInsertReference={handleInsertReference}
                  onHighlightedItemCodeChange={handleSetHighlightedItemCode}
                  currentHighlightedItemCode={highlightedItemCode}
                  onScrollToMedicationReady={scrollToMedicationRef}
                  reviewId={review.id}
                  activeTab={activeFeedbackTab}
                  onTabChange={onFeedbackTabChange}
                />
              </ReviewContentContainer>
            </PanelContent>
          </Panel>
        </PanelGroup>
      </Card>
      <ReferenceInfoModal
        isOpen={isReferenceInfoModalOpen}
        onClose={() => setIsReferenceInfoModalOpen(false)}
      />
      {historyViewerOpen && (
        <Suspense fallback={null}>
          <PrescriptionHistoryViewerModal
            isOpen={historyViewerOpen}
            onClose={() => setHistoryViewerOpen(false)}
            prescriptions={snapPrescriptions}
            patientId={review.patientId}
            readOnly
          />
        </Suspense>
      )}
    </OuterContainer>
  );
};

export default AIReviewDetailTemplate;
